import type { Address, Hex } from 'viem'
import type { ShieldedWalletClient } from 'seismic-viem'

type ShieldedWriteParams = {
  safeAddress: Address
  /** Encoded calldata to send to the Safe (e.g. `execTransaction` or `approveHash`). */
  data: Hex
  /** Explicit gas limit. Required: without it the client would run `eth_estimateGas`, which reverts
   * with the zeroed-`from` GS025 on Seismic. */
  gas: bigint
}

/**
 * Submits a Safe call to a shielded chain as a Seismic typed-data (0x4A, messageVersion 2)
 * transaction. With a json-rpc (MetaMask) account, seismic-viem signs the EIP-712 envelope via
 * `eth_signTypedData_v4` and submits the raw transaction — MetaMask never runs its own pre-flight
 * simulation, so the misleading `-32603` (zeroed-`from` GS025) is avoided. The calldata is encrypted
 * in transit and decrypted by the TEE before EVM execution, so the Safe contract still receives the
 * plaintext `execTransaction` args. `msg.sender` is the signer, so the GS025 check passes.
 */
export const executeShieldedExec = (
  client: ShieldedWalletClient,
  { safeAddress, data, gas }: ShieldedWriteParams,
): Promise<Hex> => client.sendShieldedTransaction({ chain: undefined, to: safeAddress, data, gas })

/**
 * Fallback unblock for sole-owner direct execute: submit `approveHash(safeTxHash)` as a shielded
 * write. Once the hash is approved on-chain, `execTransaction`'s GS025 check passes via
 * `approvedHashes` regardless of `msg.sender`.
 */
export const approveShieldedHash = executeShieldedExec
